import GreyLogo from "@/assets/images/grey-logo.svg";
import { BlurBackgroundContainer } from "@/component/BlurBackgroundContainer";
import { memo, useCallback, useState } from "react";

interface AssetItemProps {
  src: string;
  previewSrc?: string;
  alt: string;
  mediaType: "image" | "video";
  index?: number;
  /** Label displayed at the bottom of the image/video */
  bottomLabel?: string;
  isSelected?: boolean;
  onClick: () => void;
  stopSignal?: number;
}

const PLAY_BUTTON_STYLE = {
  container: "absolute left-3 bottom-3 w-[28px] h-[28px] play-button",
  icon: "w-[17px] h-[17px]",
};

export const AssetItem = memo(function AssetItem(props: AssetItemProps) {
  const {
    src,
    previewSrc,
    alt,
    mediaType,
    index,
    bottomLabel,
    isSelected,
    onClick,
    stopSignal,
  } = props;

  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  const handleVideoClick = useCallback(
    (e: React.MouseEvent) => {
      if (mediaType === "video") {
        e.stopPropagation();
      }
    },
    [mediaType],
  );

  return (
    <div
      onClick={onClick}
      className="relative overflow-hidden group cursor-pointer rounded-lg"
    >
      <div
        className="aspect-video bg-background-elevated rounded-lg overflow-hidden w-full relative"
        onClick={handleVideoClick}
      >
        {!src || hasError ? (
          <div className="w-full h-full object-cover rounded-lg flex items-center justify-center">
            <GreyLogo className="w-16 h-16 text-text-secondary" />
          </div>
        ) : (
          <BlurBackgroundContainer
            mediaType={mediaType}
            src={src}
            previewSrc={previewSrc || ""}
            height="100%"
            alt={alt}
            className="w-full h-full aspect-video shrink-0"
            showControls={mediaType === "video" ? false : undefined}
            onError={handleError}
            playButtonStyle={PLAY_BUTTON_STYLE}
            stopSignal={stopSignal}
          />
        )}

        {/* Bottom label overlay */}
        {bottomLabel && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent z-10">
            <p className="text-white text-sm font-medium truncate">
              {bottomLabel}
            </p>
          </div>
        )}
      </div>

      {index !== undefined && (
        <div className="absolute top-2 left-2 w-6 h-6 bg-[rgba(0,0,0,0.15)] text-white rounded-sm flex items-center justify-center font-medium text-xs z-[100] backdrop-blur-xl">
          {index + 1}
        </div>
      )}

      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-[#22c55e] rounded-full flex items-center justify-center z-[100]">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      )}
    </div>
  );
});
